import * as Si from "react-icons/si";
import * as Di from "react-icons/di";
import * as Fa from "react-icons/fa";
import * as Custom from "./customIcons";

// Icon libraries we can pull from. `pack` on each entry selects one (default "si").
// "custom" holds hand-drawn SVGs for brands not yet in the installed react-icons.
const PACKS = { si: Si, di: Di, fa: Fa, custom: Custom };

// Catalog of selectable tech icons. `slug` is what gets stored in skill.icon;
// `pack` picks the react-icons library (default "si"); `icon` is the export name
// within that pack; `color` is the official brand hex used to render the logo in
// full color. The list is filtered to icons that actually exist in the installed
// react-icons version, so the picker never renders a blank entry.
const RAW = [
  // Languages
  { slug: "javascript", label: "JavaScript", icon: "SiJavascript", color: "#F7DF1E" },
  { slug: "typescript", label: "TypeScript", icon: "SiTypescript", color: "#3178C6" },
  { slug: "python", label: "Python", icon: "SiPython", color: "#3776AB" },
  { slug: "php", label: "PHP", icon: "SiPhp", color: "#777BB4" },
  { slug: "java", label: "Java", icon: "SiOpenjdk", color: "#437291" },
  { slug: "csharp", label: "C#", icon: "SiCsharp", color: "#239120" },
  { slug: "cpp", label: "C++", icon: "SiCplusplus", color: "#00599C" },
  { slug: "c", label: "C", icon: "SiC", color: "#A8B9CC" },
  { slug: "ruby", label: "Ruby", icon: "SiRuby", color: "#CC342D" },
  { slug: "go", label: "Go", icon: "SiGo", color: "#00ADD8" },
  { slug: "rust", label: "Rust", icon: "SiRust", color: "#000000" },
  { slug: "kotlin", label: "Kotlin", icon: "SiKotlin", color: "#7F52FF" },
  { slug: "swift", label: "Swift", icon: "SiSwift", color: "#F05138" },
  { slug: "dart", label: "Dart", icon: "SiDart", color: "#0175C2" },
  { slug: "scala", label: "Scala", icon: "SiScala", color: "#DC322F" },
  { slug: "elixir", label: "Elixir", icon: "SiElixir", color: "#4B275F" },
  { slug: "haskell", label: "Haskell", icon: "SiHaskell", color: "#5D4F85" },
  { slug: "lua", label: "Lua", icon: "SiLua", color: "#2C2D72" },
  { slug: "perl", label: "Perl", icon: "SiPerl", color: "#39457E" },
  { slug: "r", label: "R", icon: "SiR", color: "#276DC3" },
  { slug: "bash", label: "Bash", icon: "SiGnubash", color: "#4EAA25" },
  { slug: "coffeescript", label: "CoffeeScript", icon: "SiCoffeescript", color: "#2F2625" },
  // Markup / styling
  { slug: "html5", label: "HTML5", icon: "SiHtml5", color: "#E34F26" },
  { slug: "css3", label: "CSS3", icon: "SiCss3", color: "#1572B6" },
  { slug: "sass", label: "Sass", icon: "SiSass", color: "#CC6699" },
  { slug: "less", label: "Less", icon: "SiLess", color: "#1D365D" },
  { slug: "tailwind", label: "Tailwind", icon: "SiTailwindcss", color: "#06B6D4" },
  { slug: "bootstrap", label: "Bootstrap", icon: "SiBootstrap", color: "#7952B3" },
  { slug: "styledcomponents", label: "Styled Comp.", icon: "SiStyledcomponents", color: "#DB7093" },
  { slug: "chakraui", label: "Chakra UI", icon: "SiChakraui", color: "#319795" },
  { slug: "mui", label: "Material UI", icon: "SiMui", color: "#007FFF" },
  // Frontend frameworks / tooling
  { slug: "react", label: "React", icon: "SiReact", color: "#61DAFB" },
  { slug: "angular", label: "Angular", icon: "SiAngular", color: "#DD0031" },
  { slug: "vue", label: "Vue", icon: "SiVuedotjs", color: "#4FC08D" },
  { slug: "svelte", label: "Svelte", icon: "SiSvelte", color: "#FF3E00" },
  { slug: "nextjs", label: "Next.js", icon: "SiNextdotjs", color: "#000000" },
  { slug: "nuxtjs", label: "Nuxt.js", icon: "SiNuxtdotjs", color: "#00DC82" },
  { slug: "gatsby", label: "Gatsby", icon: "SiGatsby", color: "#663399" },
  { slug: "redux", label: "Redux", icon: "SiRedux", color: "#764ABC" },
  { slug: "jquery", label: "jQuery", icon: "SiJquery", color: "#0769AD" },
  { slug: "vite", label: "Vite", icon: "SiVite", color: "#646CFF" },
  { slug: "webpack", label: "Webpack", icon: "SiWebpack", color: "#8DD6F9" },
  { slug: "babel", label: "Babel", icon: "SiBabel", color: "#F9DC3E" },
  { slug: "storybook", label: "Storybook", icon: "SiStorybook", color: "#FF4785" },
  // Backend frameworks / runtimes
  { slug: "nodejs", label: "Node.js", icon: "SiNodedotjs", color: "#5FA04E" },
  { slug: "express", label: "Express", icon: "SiExpress", color: "#000000" },
  { slug: "nestjs", label: "NestJS", icon: "SiNestjs", color: "#E0234E" },
  { slug: "dotnet", label: ".NET", icon: "SiDotnet", color: "#512BD4" },
  { slug: "django", label: "Django", icon: "SiDjango", color: "#092E20" },
  { slug: "flask", label: "Flask", icon: "SiFlask", color: "#000000" },
  { slug: "fastapi", label: "FastAPI", icon: "SiFastapi", color: "#009688" },
  { slug: "spring", label: "Spring", icon: "SiSpring", color: "#6DB33F" },
  { slug: "laravel", label: "Laravel", icon: "SiLaravel", color: "#FF2D20" },
  { slug: "rails", label: "Rails", icon: "SiRubyonrails", color: "#D30001" },
  { slug: "graphql", label: "GraphQL", icon: "SiGraphql", color: "#E10098" },
  { slug: "socketio", label: "Socket.IO", icon: "SiSocketdotio", color: "#010101" },
  { slug: "kafka", label: "Kafka", icon: "SiApachekafka", color: "#231F20" },
  // Mobile
  { slug: "flutter", label: "Flutter", icon: "SiFlutter", color: "#02569B" },
  { slug: "ionic", label: "Ionic", icon: "SiIonic", color: "#3880FF" },
  { slug: "android", label: "Android", icon: "SiAndroid", color: "#3DDC84" },
  { slug: "apple", label: "iOS", icon: "SiApple", color: "#000000" },
  // Databases
  { slug: "sql", label: "SQL", icon: "SiMysql", color: "#4479A1" },
  { slug: "mysql", label: "MySQL", icon: "SiMysql", color: "#4479A1" },
  { slug: "mariadb", label: "MariaDB", icon: "SiMariadb", color: "#003545" },
  { slug: "postgresql", label: "PostgreSQL", icon: "SiPostgresql", color: "#4169E1" },
  { slug: "sqlserver", label: "SQL Server", icon: "SiMicrosoftsqlserver", color: "#CC2927" },
  { slug: "oracle", label: "Oracle", icon: "SiOracle", color: "#F80000" },
  { slug: "mongodb", label: "MongoDB", icon: "SiMongodb", color: "#47A248" },
  { slug: "sqlite", label: "SQLite", icon: "SiSqlite", color: "#003B57" },
  { slug: "redis", label: "Redis", icon: "SiRedis", color: "#FF4438" },
  { slug: "elasticsearch", label: "Elasticsearch", icon: "SiElasticsearch", color: "#005571" },
  { slug: "supabase", label: "Supabase", icon: "SiSupabase", color: "#3FCF8E" },
  { slug: "firebase", label: "Firebase", icon: "SiFirebase", color: "#FFCA28" },
  { slug: "prisma", label: "Prisma", icon: "SiPrisma", color: "#2D3748" },
  { slug: "sequelize", label: "Sequelize", icon: "SiSequelize", color: "#52B0E7" },
  // Cloud / DevOps
  { slug: "docker", label: "Docker", icon: "SiDocker", color: "#2496ED" },
  { slug: "kubernetes", label: "Kubernetes", icon: "SiKubernetes", color: "#326CE5" },
  { slug: "aws", label: "AWS", icon: "SiAmazonaws", color: "#232F3E" },
  { slug: "azure", label: "Azure", icon: "SiMicrosoftazure", color: "#0078D4" },
  { slug: "gcp", label: "Google Cloud", icon: "SiGooglecloud", color: "#4285F4" },
  { slug: "vercel", label: "Vercel", icon: "SiVercel", color: "#000000" },
  { slug: "netlify", label: "Netlify", icon: "SiNetlify", color: "#00C7B7" },
  { slug: "heroku", label: "Heroku", icon: "SiHeroku", color: "#430098" },
  { slug: "digitalocean", label: "DigitalOcean", icon: "SiDigitalocean", color: "#0080FF" },
  { slug: "nginx", label: "Nginx", icon: "SiNginx", color: "#009639" },
  { slug: "linux", label: "Linux", icon: "SiLinux", color: "#FCC624" },
  { slug: "ubuntu", label: "Ubuntu", icon: "SiUbuntu", color: "#E95420" },
  // Tools / platforms
  { slug: "git", label: "Git", icon: "SiGit", color: "#F05032" },
  { slug: "github", label: "GitHub", icon: "SiGithub", color: "#181717" },
  { slug: "gitlab", label: "GitLab", icon: "SiGitlab", color: "#FC6D26" },
  { slug: "bitbucket", label: "Bitbucket", icon: "SiBitbucket", color: "#0052CC" },
  { slug: "jira", label: "Jira", icon: "SiJira", color: "#0052CC" },
  { slug: "postman", label: "Postman", icon: "SiPostman", color: "#FF6C37" },
  { slug: "figma", label: "Figma", icon: "SiFigma", color: "#F24E1E" },
  { slug: "npm", label: "npm", icon: "SiNpm", color: "#CB3837" },
  { slug: "yarn", label: "Yarn", icon: "SiYarn", color: "#2C8EBB" },
  { slug: "vscode", label: "VS Code", icon: "SiVisualstudiocode", color: "#007ACC" },
  { slug: "wordpress", label: "WordPress", icon: "SiWordpress", color: "#21759B" },
  { slug: "shopify", label: "Shopify", icon: "SiShopify", color: "#7AB55C" },
  { slug: "stripe", label: "Stripe", icon: "SiStripe", color: "#635BFF" },
  // More languages
  { slug: "clojure", label: "Clojure", icon: "SiClojure", color: "#5881D8" },
  { slug: "erlang", label: "Erlang", icon: "SiErlang", color: "#A90533" },
  { slug: "fsharp", label: "F#", icon: "SiFsharp", color: "#378BBA" },
  { slug: "groovy", label: "Groovy", icon: "SiApachegroovy", color: "#4298B8" },
  { slug: "julia", label: "Julia", icon: "SiJulia", color: "#9558B2" },
  { slug: "solidity", label: "Solidity", icon: "SiSolidity", color: "#363636" },
  { slug: "zig", label: "Zig", icon: "SiZig", color: "#F7A41D" },
  { slug: "crystal", label: "Crystal", icon: "SiCrystal", color: "#000000" },
  { slug: "nim", label: "Nim", icon: "SiNim", color: "#FFE953" },
  { slug: "ocaml", label: "OCaml", icon: "SiOcaml", color: "#EC6813" },
  { slug: "fortran", label: "Fortran", icon: "SiFortran", color: "#734F96" },
  { slug: "assemblyscript", label: "AssemblyScript", icon: "SiAssemblyscript", color: "#007AAC" },
  { slug: "wasm", label: "WebAssembly", icon: "SiWebassembly", color: "#654FF0" },
  // Data / config formats
  { slug: "json", label: "JSON", icon: "SiJson", color: "#000000" },
  { slug: "yaml", label: "YAML", icon: "SiYaml", color: "#CB171E" },
  { slug: "markdown", label: "Markdown", icon: "SiMarkdown", color: "#000000" },
  { slug: "latex", label: "LaTeX", icon: "SiLatex", color: "#008080" },
  // More frontend
  { slug: "solidjs", label: "SolidJS", icon: "SiSolid", color: "#2C4F7C" },
  { slug: "alpinejs", label: "Alpine.js", icon: "SiAlpinedotjs", color: "#8BC0D0" },
  { slug: "astro", label: "Astro", icon: "SiAstro", color: "#FF5D01" },
  { slug: "remix", label: "Remix", icon: "SiRemix", color: "#000000" },
  { slug: "ember", label: "Ember.js", icon: "SiEmberdotjs", color: "#E04E39" },
  { slug: "preact", label: "Preact", icon: "SiPreact", color: "#673AB8" },
  { slug: "backbone", label: "Backbone.js", icon: "SiBackbonedotjs", color: "#0071B5" },
  { slug: "threejs", label: "Three.js", icon: "SiThreedotjs", color: "#000000" },
  { slug: "d3", label: "D3.js", icon: "SiD3Dotjs", color: "#F9A03C" },
  { slug: "electron", label: "Electron", icon: "SiElectron", color: "#47848F" },
  { slug: "capacitor", label: "Capacitor", icon: "SiCapacitor", color: "#119EFF" },
  { slug: "pwa", label: "PWA", icon: "SiPwa", color: "#5A0FC8" },
  // Testing / quality
  { slug: "jest", label: "Jest", icon: "SiJest", color: "#C21325" },
  { slug: "vitest", label: "Vitest", icon: "SiVitest", color: "#6E9F18" },
  { slug: "cypress", label: "Cypress", icon: "SiCypress", color: "#69D3A7" },
  { slug: "playwright", label: "Playwright", icon: "SiPlaywright", color: "#2EAD33" },
  { slug: "testinglibrary", label: "Testing Library", icon: "SiTestinglibrary", color: "#E33332" },
  { slug: "selenium", label: "Selenium", icon: "SiSelenium", color: "#43B02A" },
  { slug: "eslint", label: "ESLint", icon: "SiEslint", color: "#4B32C3" },
  { slug: "prettier", label: "Prettier", icon: "SiPrettier", color: "#F7B93E" },
  // Build / package tooling
  { slug: "pnpm", label: "pnpm", icon: "SiPnpm", color: "#F69220" },
  { slug: "turborepo", label: "Turborepo", icon: "SiTurborepo", color: "#EF4444" },
  { slug: "rollup", label: "Rollup", icon: "SiRollupdotjs", color: "#EC4A3F" },
  { slug: "esbuild", label: "esbuild", icon: "SiEsbuild", color: "#FFCF00" },
  // More backend
  { slug: "deno", label: "Deno", icon: "SiDeno", color: "#000000" },
  { slug: "bun", label: "Bun", icon: "SiBun", color: "#000000" },
  { slug: "adonisjs", label: "AdonisJS", icon: "SiAdonisjs", color: "#5A45FF" },
  { slug: "fastify", label: "Fastify", icon: "SiFastify", color: "#000000" },
  { slug: "strapi", label: "Strapi", icon: "SiStrapi", color: "#4945FF" },
  { slug: "apollo", label: "Apollo", icon: "SiApollographql", color: "#311C87" },
  { slug: "trpc", label: "tRPC", icon: "SiTrpc", color: "#2596BE" },
  { slug: "hasura", label: "Hasura", icon: "SiHasura", color: "#1EB4D4" },
  { slug: "symfony", label: "Symfony", icon: "SiSymfony", color: "#000000" },
  { slug: "codeigniter", label: "CodeIgniter", icon: "SiCodeigniter", color: "#EF4223" },
  { slug: "phoenix", label: "Phoenix", icon: "SiPhoenixframework", color: "#FD4F00" },
  { slug: "rabbitmq", label: "RabbitMQ", icon: "SiRabbitmq", color: "#FF6600" },
  { slug: "celery", label: "Celery", icon: "SiCelery", color: "#37814A" },
  // Data / ML
  { slug: "pandas", label: "Pandas", icon: "SiPandas", color: "#150458" },
  { slug: "numpy", label: "NumPy", icon: "SiNumpy", color: "#013243" },
  { slug: "scikitlearn", label: "scikit-learn", icon: "SiScikitlearn", color: "#F7931E" },
  { slug: "tensorflow", label: "TensorFlow", icon: "SiTensorflow", color: "#FF6F00" },
  { slug: "pytorch", label: "PyTorch", icon: "SiPytorch", color: "#EE4C2C" },
  { slug: "keras", label: "Keras", icon: "SiKeras", color: "#D00000" },
  { slug: "opencv", label: "OpenCV", icon: "SiOpencv", color: "#5C3EE8" },
  { slug: "jupyter", label: "Jupyter", icon: "SiJupyter", color: "#F37626" },
  { slug: "anaconda", label: "Anaconda", icon: "SiAnaconda", color: "#44A833" },
  { slug: "openai", label: "OpenAI", icon: "SiOpenai", color: "#412991" },
  // More databases
  { slug: "cassandra", label: "Cassandra", icon: "SiApachecassandra", color: "#1287B1" },
  { slug: "couchdb", label: "CouchDB", icon: "SiApachecouchdb", color: "#E42528" },
  { slug: "influxdb", label: "InfluxDB", icon: "SiInfluxdb", color: "#22ADF6" },
  { slug: "planetscale", label: "PlanetScale", icon: "SiPlanetscale", color: "#000000" },
  { slug: "cockroachdb", label: "CockroachDB", icon: "SiCockroachlabs", color: "#6933FF" },
  { slug: "dynamodb", label: "DynamoDB", icon: "SiAmazondynamodb", color: "#4053D6" },
  // More cloud / DevOps
  { slug: "terraform", label: "Terraform", icon: "SiTerraform", color: "#7B42BC" },
  { slug: "ansible", label: "Ansible", icon: "SiAnsible", color: "#EE0000" },
  { slug: "jenkins", label: "Jenkins", icon: "SiJenkins", color: "#D24939" },
  { slug: "githubactions", label: "GitHub Actions", icon: "SiGithubactions", color: "#2088FF" },
  { slug: "circleci", label: "CircleCI", icon: "SiCircleci", color: "#343434" },
  { slug: "travisci", label: "Travis CI", icon: "SiTravisci", color: "#3EAAAF" },
  { slug: "vagrant", label: "Vagrant", icon: "SiVagrant", color: "#1868F2" },
  { slug: "prometheus", label: "Prometheus", icon: "SiPrometheus", color: "#E6522C" },
  { slug: "grafana", label: "Grafana", icon: "SiGrafana", color: "#F46800" },
  { slug: "datadog", label: "Datadog", icon: "SiDatadog", color: "#632CA6" },
  { slug: "sentry", label: "Sentry", icon: "SiSentry", color: "#362D59" },
  { slug: "cloudflare", label: "Cloudflare", icon: "SiCloudflare", color: "#F38020" },
  { slug: "railway", label: "Railway", icon: "SiRailway", color: "#0B0D0E" },
  { slug: "render", label: "Render", icon: "SiRender", color: "#46E3B7" },
  { slug: "helm", label: "Helm", icon: "SiHelm", color: "#0F1689" },
  { slug: "podman", label: "Podman", icon: "SiPodman", color: "#892CA0" },
  // Auth / APIs / services
  { slug: "jwt", label: "JWT", icon: "SiJsonwebtokens", color: "#000000" },
  { slug: "auth0", label: "Auth0", icon: "SiAuth0", color: "#EB5424" },
  { slug: "okta", label: "Okta", icon: "SiOkta", color: "#007DC1" },
  { slug: "twilio", label: "Twilio", icon: "SiTwilio", color: "#F22F46" },
  { slug: "mailchimp", label: "Mailchimp", icon: "SiMailchimp", color: "#FFE01B" },
  { slug: "algolia", label: "Algolia", icon: "SiAlgolia", color: "#003DFF" },
  { slug: "contentful", label: "Contentful", icon: "SiContentful", color: "#2478CC" },
  { slug: "sanity", label: "Sanity", icon: "SiSanity", color: "#F03E2F" },
  { slug: "swagger", label: "Swagger", icon: "SiSwagger", color: "#85EA2D" },
  { slug: "salesforce", label: "Salesforce", icon: "SiSalesforce", color: "#00A1E0" },
  // More tools / editors / design
  { slug: "slack", label: "Slack", icon: "SiSlack", color: "#4A154B" },
  { slug: "discord", label: "Discord", icon: "SiDiscord", color: "#5865F2" },
  { slug: "notion", label: "Notion", icon: "SiNotion", color: "#000000" },
  { slug: "trello", label: "Trello", icon: "SiTrello", color: "#0052CC" },
  { slug: "confluence", label: "Confluence", icon: "SiConfluence", color: "#172B4D" },
  { slug: "vim", label: "Vim", icon: "SiVim", color: "#019733" },
  { slug: "neovim", label: "Neovim", icon: "SiNeovim", color: "#57A143" },
  { slug: "intellij", label: "IntelliJ IDEA", icon: "SiIntellijidea", color: "#000000" },
  { slug: "webstorm", label: "WebStorm", icon: "SiWebstorm", color: "#000000" },
  { slug: "pycharm", label: "PyCharm", icon: "SiPycharm", color: "#000000" },
  { slug: "androidstudio", label: "Android Studio", icon: "SiAndroidstudio", color: "#3DDC84" },
  { slug: "xcode", label: "Xcode", icon: "SiXcode", color: "#147EFB" },
  { slug: "insomnia", label: "Insomnia", icon: "SiInsomnia", color: "#4000BF" },
  { slug: "obsidian", label: "Obsidian", icon: "SiObsidian", color: "#7C3AED" },
  { slug: "photoshop", label: "Photoshop", icon: "SiAdobephotoshop", color: "#31A8FF" },
  { slug: "illustrator", label: "Illustrator", icon: "SiAdobeillustrator", color: "#FF9A00" },
  { slug: "adobexd", label: "Adobe XD", icon: "SiAdobexd", color: "#FF61F6" },
  { slug: "sketch", label: "Sketch", icon: "SiSketch", color: "#F7B500" },
  { slug: "framer", label: "Framer", icon: "SiFramer", color: "#0055FF" },
  { slug: "webflow", label: "Webflow", icon: "SiWebflow", color: "#146EF5" },
  { slug: "canva", label: "Canva", icon: "SiCanva", color: "#00C4CC" },
  { slug: "blender", label: "Blender", icon: "SiBlender", color: "#E87D0D" },
  { slug: "unity", label: "Unity", icon: "SiUnity", color: "#000000" },
  { slug: "unrealengine", label: "Unreal Engine", icon: "SiUnrealengine", color: "#0E1128" },
  { slug: "godot", label: "Godot", icon: "SiGodotengine", color: "#478CBF" },
  { slug: "arduino", label: "Arduino", icon: "SiArduino", color: "#00979D" },
  { slug: "raspberrypi", label: "Raspberry Pi", icon: "SiRaspberrypi", color: "#A22846" },
  { slug: "woocommerce", label: "WooCommerce", icon: "SiWoocommerce", color: "#96588A" },
  { slug: "magento", label: "Magento", icon: "SiMagento", color: "#EE672F" },
  // AI / dev assistants (custom SVGs for brands not yet in react-icons 4.7.1)
  { slug: "claude", label: "Claude", pack: "custom", icon: "SiClaude", color: "#D97757" },
  { slug: "claudecode", label: "Claude Code", pack: "custom", icon: "SiClaude", color: "#D97757" },
  { slug: "gemini", label: "Gemini", pack: "custom", icon: "SiGemini", color: "#8E75F8" },
  { slug: "copilot", label: "GitHub Copilot", pack: "custom", icon: "SiCopilot", color: "#000000" },
  { slug: "cursor", label: "Cursor", pack: "custom", icon: "SiCursor", color: "#000000" },
  // AI tools available in the installed libraries
  { slug: "chatgpt", label: "ChatGPT", icon: "SiOpenai", color: "#74AA9C" },
  { slug: "codeium", label: "Codeium", icon: "SiCodeium", color: "#09B6A2" },
  { slug: "replit", label: "Replit", icon: "SiReplit", color: "#F26207" },
  { slug: "bard", label: "Google Bard", icon: "SiGooglebard", color: "#886FBF" },
  { slug: "ai", label: "AI", pack: "fa", icon: "FaRobot", color: "#10A37F" },
  { slug: "brain", label: "AI / Brain", pack: "fa", icon: "FaBrain", color: "#EC4899" },
];

// Only keep icons whose component actually exists in the installed library.
export const TECH_ICONS = RAW.filter((t) => (PACKS[t.pack || "si"] || {})[t.icon]);

// Resolve a stored slug to its icon component (or null).
export function techIcon(slug) {
  if (!slug) return null;
  const entry = TECH_ICONS.find((t) => t.slug === slug.toLowerCase());
  if (!entry) return null;
  return (PACKS[entry.pack || "si"] || {})[entry.icon] || null;
}

// Resolve a stored slug to its official brand color (or null).
export function techColor(slug) {
  if (!slug) return null;
  const entry = TECH_ICONS.find((t) => t.slug === slug.toLowerCase());
  return entry ? entry.color : null;
}
